import { Component } from '@angular/core';
import { FormControl } from '@angular/forms';
import { TranslateService } from '../../../services/translate/translate.service';
import { ValidationRule } from '../../../models/validation-rules/validation-rules';
import { ValidationResult } from '../../../utils/form-validation';
import {
	CustomSegmentedControlComponent,
	SegmentedControlColor,
	SegmentedControlOption,
	SegmentedControlSize,
	SegmentedControlValue,
	SegmentedControlVariant,
} from '../../../shared-components/custom-segmented-control/custom-segmented-control.component';

export type PlaygroundWidth = 'full' | 'w480' | 'w360' | 'w260' | 'w180';

@Component({
	selector: 'app-custom-segmented-control-showcase',
	templateUrl: './custom-segmented-control-showcase.component.html',
	styleUrls: ['./custom-segmented-control-showcase.component.scss'],
	standalone: false,
})
export class CustomSegmentedControlShowcaseComponent {
	public periodValue: SegmentedControlValue = 'week';
	public viewValue: SegmentedControlValue = 'list';
	public alignValue: SegmentedControlValue = 'left';
	public pageSizeValue: SegmentedControlValue = 20;
	public colorValue: SegmentedControlValue = 'a';
	public sizeValue: SegmentedControlValue = 'a';
	public variantValue: SegmentedControlValue = 'a';
	public outlinedColorValue: SegmentedControlValue = 'a';
	public statesValue: SegmentedControlValue = 'active';
	public formValue: SegmentedControlValue | null = null;
	public formValid: boolean = false;
	public readonlyValue: SegmentedControlValue = 'approved';
	public playgroundTextValue: SegmentedControlValue = 'pending';
	public playgroundIconValue: SegmentedControlValue = 'pending';
	public playgroundFullWidthValue: SegmentedControlValue = 'pending';
	public playgroundToolbarValue: SegmentedControlValue = 'list';
	public playgroundWidth: PlaygroundWidth = 'full';
	public showArchived: boolean = false;

	public readonly periodOptions: SegmentedControlOption[] = [
		{ value: 'day', label: 'Day' },
		{ value: 'week', label: 'Week' },
		{ value: 'month', label: 'Month' },
		{ value: 'year', label: 'Year' },
	];

	public readonly viewOptions: SegmentedControlOption[] = [
		{ value: 'list', label: 'List', icon: 'fa-solid fa-list' },
		{ value: 'grid', label: 'Grid', icon: 'fa-solid fa-grip' },
		{ value: 'calendar', label: 'Calendar', icon: 'fa-solid fa-calendar' },
	];

	public readonly alignOptions: SegmentedControlOption[] = [
		{ value: 'left', label: 'Align left', icon: 'fa-solid fa-align-left' },
		{ value: 'center', label: 'Align center', icon: 'fa-solid fa-align-center' },
		{ value: 'right', label: 'Align right', icon: 'fa-solid fa-align-right' },
		{ value: 'justify', label: 'Justify', icon: 'fa-solid fa-align-justify' },
	];

	public readonly pageSizeOptions: SegmentedControlOption[] = [
		{ value: 10, label: '10' },
		{ value: 20, label: '20' },
		{ value: 50, label: '50' },
		{ value: 100, label: '100' },
	];

	public readonly colors: SegmentedControlColor[] = ['primary', 'success', 'info', 'warning', 'danger', 'neutral'];

	public readonly sizes: SegmentedControlSize[] = ['small', 'medium', 'large'];

	public readonly variants: SegmentedControlVariant[] = ['standard', 'outlined'];

	public readonly genericOptions: SegmentedControlOption[] = [
		{ value: 'a', label: 'Option A' },
		{ value: 'b', label: 'Option B' },
		{ value: 'c', label: 'Option C' },
	];

	public readonly statesOptions: SegmentedControlOption[] = [
		{ value: 'active', label: 'Active' },
		{ value: 'pending', label: 'Pending', disabled: true },
		{ value: 'archived', label: 'Archived', condition: false },
		{ value: 'deleted', label: 'Deleted' },
	];

	public readonly disabledOptions: SegmentedControlOption[] = [
		{ value: 'x', label: 'First' },
		{ value: 'y', label: 'Second' },
	];

	public readonly playgroundTextOptions: SegmentedControlOption[] = [
		{ value: 'pending', label: 'Pending approval' },
		{ value: 'approved', label: 'Approved' },
		{ value: 'partial', label: 'Partially approved' },
		{ value: 'rejected', label: 'Rejected' },
	];

	public readonly playgroundIconOptions: SegmentedControlOption[] = [
		{ value: 'pending', label: 'Pending approval', icon: 'fa-solid fa-clock' },
		{ value: 'approved', label: 'Approved', icon: 'fa-solid fa-check' },
		{ value: 'partial', label: 'Partially approved', icon: 'fa-solid fa-check-double' },
		{ value: 'rejected', label: 'Rejected', icon: 'fa-solid fa-xmark' },
	];

	public readonly playgroundPresets: { id: PlaygroundWidth; label: string }[] = [
		{ id: 'full', label: '100%' },
		{ id: 'w480', label: '480px' },
		{ id: 'w360', label: '360px' },
		{ id: 'w260', label: '260px' },
		{ id: 'w180', label: '180px' },
	];

	public requiredPlanRules: ValidationRule[] = [];

	public reactiveControl = new FormControl<SegmentedControlValue | null>('week');

	constructor(private translateService: TranslateService) {
		this.requiredPlanRules = [CustomSegmentedControlComponent.createRules(this.translateService).required('Please choose a plan')];
	}

	public get conditionalOptions(): SegmentedControlOption[] {
		return [
			{ value: 'active', label: 'Active' },
			{ value: 'archived', label: 'Archived', condition: this.showArchived },
			{ value: 'deleted', label: 'Deleted' },
		];
	}

	public toggleArchived(): void {
		this.showArchived = !this.showArchived;
		if (!this.showArchived && this.statesValue === 'archived') {
			this.statesValue = 'active';
		}
	}

	public onValidationChange(result: ValidationResult): void {
		this.formValid = result.isValid;
	}

	public toggleReactiveDisabled(): void {
		if (this.reactiveControl.disabled) {
			this.reactiveControl.enable();
		} else {
			this.reactiveControl.disable();
		}
	}

	public setPlaygroundWidth(width: PlaygroundWidth): void {
		this.playgroundWidth = width;
	}

	public get playgroundClass(): string {
		return this.playgroundWidth === 'full' ? '' : `segmented-playground--${this.playgroundWidth}`;
	}

	public get playgroundWidthLabel(): string {
		return this.playgroundPresets.find((preset) => preset.id === this.playgroundWidth)?.label ?? '100%';
	}
}
